/**
 * Estado global do gate de biometria no cold start (estilo "Betano").
 *
 * - "Já disparou nesta sessão": garante UMA execução por sessão da WebView.
 *   Persistido em `sessionStorage` (chave `biometric_gate_fired`) — sobrevive
 *   a `window.location.reload()` (pull-to-refresh), mas é limpo quando a
 *   WebView/aba é destruída (app fechado = cold start real). Sem isso, todo
 *   reload recriava o contexto JS e re-armava o gate, fazendo a biometria
 *   ser pedida em todo pull-to-refresh.
 * - `isGateInProgress`: ref reativo compartilhado. Durante o gate
 *   (entre o disparo de `unlock()` e a decisão final), consumidores
 *   como o `logout()` do auth store suprimem efeitos colaterais de
 *   401 — toasts de "sessão expirada" e redirects automáticos — para
 *   não atropelarem o prompt biométrico que está na tela. Fica em memória
 *   de propósito (estado vivo da execução atual, não deve persistir).
 */

const GATE_FIRED_SESSION_KEY = 'biometric_gate_fired';

const isGateInProgress = ref(false);

function hasFiredThisSession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return sessionStorage.getItem(GATE_FIRED_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markFiredThisSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(GATE_FIRED_SESSION_KEY, '1');
  } catch {}
}

function unmarkFiredThisSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(GATE_FIRED_SESSION_KEY);
  } catch {}
}

export const useBiometricColdStartGate = () => {
  function shouldFire(): boolean {
    return !hasFiredThisSession();
  }

  function markFired(): void {
    markFiredThisSession();
  }

  /**
   * Reabre o gate para disparar de novo nesta sessão. Usado quando o gate
   * abortou por falta de conexão — não é uma decisão final, então deve poder
   * rodar de novo quando a conectividade voltar (listener `online`).
   */
  function unmarkFired(): void {
    unmarkFiredThisSession();
  }

  function beginGate(): void {
    isGateInProgress.value = true;
  }

  function endGate(): void {
    isGateInProgress.value = false;
  }

  return {
    shouldFire,
    markFired,
    unmarkFired,
    beginGate,
    endGate,
    isGateInProgress,
  };
};
